import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { useConfirm, useDeleteFlow } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { ViewToggle } from '../../components/ViewToggle';
import { useNavActions } from '../../hooks/useNavActions';
import { ClientDetailModal, clearMaintTabMemory } from '../sampling/ClientDetailModal';
import { ContactDetailModal } from '../sampling/ContactDetailModal';
import { ProjectDetailModal } from '../sampling/ProjectDetailModal';
import {
  CLIENT_COLUMNS, CONTACT_COLUMNS, PROJECT_COLUMNS, MAINT_BASE, MAINT_FORM_NAME, matchesSearch,
  type MaintClientRow, type MaintContactRow, type MaintProjectRow, type MaintenanceKind,
} from './maintenance';
import './maintenance.css';

type Row = MaintClientRow | MaintContactRow | MaintProjectRow;

interface PageProps {
  /** The nav id — also the FormTitle / MAC key (maint-clients · maint-retired-clients · maint-contacts). */
  pageId: string;
  kind: MaintenanceKind;
  onHome: () => void;
}

const TITLES: Record<MaintenanceKind, { crumb: string; noun: string; nouns: string; empty: string }> = {
  clients: { crumb: 'Clients', noun: 'client', nouns: 'clients', empty: 'No clients found.' },
  retired: { crumb: 'Retired Clients', noun: 'client', nouns: 'clients', empty: 'No retired clients.' },
  contacts: { crumb: 'Contacts', noun: 'contact', nouns: 'contacts', empty: 'No contacts found.' },
  projects: { crumb: 'Project', noun: 'project', nouns: 'projects', empty: 'No projects found.' },
};

/**
 * Maintenance ▸ Clients · Retired Clients · Contacts · Project — the G5 "Maintenance" group.
 *
 * One page, four lists, because G5's three views are the same shape: a filtered list with New · Edit ·
 * Delete · Refresh · Export to (Retired Clients is read-only — G5 `AllowEdit/AllowNew/AllowDelete = False`).
 *
 * What is NOT rebuilt here (standing rule 8): the client / contact DETAIL — every field, the six tabs
 * (Contacts, Invoicing Contact, Projects, Collectors, Sample Sites, Notes), the retire date — is the existing
 * `ClientDetailModal` / `ContactDetailModal`, and New client is the existing quick-add, all writing through
 * `/api/v1/clients`. This page adds the G5 lists and G5's delete guards (`/api/v1/maintenance`).
 *
 * Standing rule 12: New / Edit REPLACE the list with a full-page Detail View (`MaintenanceDetailFrame` — header with prev / next
 * record arrows, the action bar top and bottom, Save · Save and Close · Save and New · Cancel). The editors keep a popup mode
 * for their other callers (Sample Registration, Quotes); here they run with `page`.
 */
export function MaintenancePage({ pageId, kind, onHome }: PageProps) {
  const notify = useNotify();
  const runDelete = useDeleteFlow();
  const { confirm } = useConfirm();
  const perm = useNavActions(pageId);
  const t = TITLES[kind];
  const isContacts = kind === 'contacts';
  const isProjects = kind === 'projects';
  const readOnly = kind === 'retired';

  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [editClient, setEditClient] = useState<string | null>(null);
  const [newClient, setNewClient] = useState(false);
  // Bumped for every blank record so "Save and New" really clears the form (the editor is keyed on it).
  const [newSeq, setNewSeq] = useState(0);
  // Bumped by the page's Refresh button so the open record is read again from the server (the editor is keyed on it).
  const [refreshSeq, setRefreshSeq] = useState(0);
  // Maximize is driven from the search row's own button, as on the Utility lists.
  const [fullGrid, setFullGrid] = useState(false);
  const [contactDlg, setContactDlg] = useState<{ clientId: string; clientName: string; type: number; contactOid: string | null } | null>(null);
  const [projectDlg, setProjectDlg] = useState<{ clientId: string; clientName: string; projectOid: string | null } | null>(null);
  // Project only: G5's "Retired" action swaps the open list for the retired projects.
  const [projectRetired, setProjectRetired] = useState(false);
  const seq = useRef(0);

  // G5's lists edit IN the row (XAF inline edit): the leading pencil turns the row into editors, Save / Undo commit it. Every column
  // that is a stored value is editable there — text boxes, DROPDOWNS for the lookups, a date picker for dates — and only the
  // computed columns (Display Name, Last Contact Date, Project, Client Address) stay plain. The Retired Clients list is read-only
  // (G5 AllowEdit = False).
  type EditSpec = NonNullable<AppColumn<Row>['edit']>;
  interface Opt { id: string; name: string }
  interface GeoOpt extends Opt { parentId: string | null }
  const [countries, setCountries] = useState<Opt[]>([]);
  const [states, setStates] = useState<GeoOpt[]>([]);
  const [cities, setCities] = useState<GeoOpt[]>([]);
  const [owners, setOwners] = useState<Opt[]>([]);
  const [projCats, setProjCats] = useState<Opt[]>([]);
  const [clientNames, setClientNames] = useState<Opt[]>([]);
  // Account numbers belong to ONE client, so they are read when that client's row opens for editing.
  const [accountsByClient, setAccountsByClient] = useState<Record<string, Opt[]>>({});
  useEffect(() => {
    if (readOnly) return;
    const get = (u: string) => api.get(u).then((r) => (r.data as { data?: unknown }).data).catch(() => undefined);
    void (async () => {
      if (isProjects) {
        const [cats, cl] = await Promise.all([get('/api/v1/clients/lookups/project-categories'), get(`${MAINT_BASE}/clients?scope=active`)]);
        setProjCats(((cats as { id: string; name: string }[] | undefined) ?? []).map((o) => ({ id: o.id, name: o.name })));
        setClientNames(((cl as { oid: string; client: string | null }[] | undefined) ?? []).filter((c) => c.client).map((c) => ({ id: c.oid, name: c.client as string })));
        return;
      }
      const [cs, st, ci, ow] = await Promise.all([
        get('/api/v1/system/states/countries'), get('/api/v1/cities/states'), get('/api/v1/cities'),
        isContacts ? Promise.resolve(undefined) : get('/api/v1/clients/lookups/owners'),
      ]);
      setCountries(((cs as { id: string; name: string }[] | undefined) ?? []).map((c) => ({ id: c.id, name: c.name })));
      setStates(((st as { id: string; shortName: string; longName: string | null; countryId: string | null }[] | undefined) ?? [])
        .map((s) => ({ id: s.id, name: s.shortName || s.longName || '', parentId: s.countryId })));
      setCities(((ci as { id: string; cityName: string | null; stateId: string | null }[] | undefined) ?? [])
        .map((c) => ({ id: c.id, name: c.cityName ?? '', parentId: c.stateId })));
      setOwners(((ow as { id: string; name: string }[] | undefined) ?? []).map((o) => ({ id: o.id, name: o.name })));
    })();
  }, [readOnly, isProjects, isContacts]);

  const idOf = (list: Opt[], name: string | undefined) => (name ? list.find((o) => o.name === name)?.id ?? null : null);
  const stateIdOf = (draft: Record<string, string>) => {
    const cid = idOf(countries, draft.country);
    return states.find((s) => s.name === draft.state && (!cid || s.parentId === cid))?.id ?? null;
  };
  const geoSpecs = useMemo<Record<string, EditSpec>>(() => ({
    country: { type: 'combo', options: countries.map((c) => c.name), resets: ['state', 'city'] },
    state: {
      type: 'combo', resets: ['city'],
      options: (_r, d) => { const cid = idOf(countries, d.country); return states.filter((s) => !cid || s.parentId === cid).map((s) => s.name); },
    },
    city: {
      type: 'combo',
      options: (_r, d) => { const sid = stateIdOf(d); return sid ? Array.from(new Set(cities.filter((c) => c.parentId === sid).map((c) => c.name))) : []; },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [countries, states, cities]);
  const PREFERRED_METHODS = ['Any', 'Email', 'Phone', 'Fax', 'Mail'];
  const YES_NO = ['Yes', 'No'];
  const text: EditSpec = { type: 'text' };
  const editSpecs = useMemo((): Record<string, EditSpec> => {
    if (readOnly) return {};
    if (isProjects) {
      return {
        projectId: text, projectName: text, projectLocation: text, projectCode: text,
        projectCategory: { type: 'combo', options: projCats.map((c) => c.name) },
        client: { type: 'combo', options: clientNames.map((c) => c.name) },
      };
    }
    if (isContacts) {
      return {
        // Address is the CLIENT's address in this list (read-only here, as in G5); the contact's own street is on the detail.
        email: text, officePhone: text, mobilePhone: text, otherPhone: text, zip: text, jobTitle: text,
        ...geoSpecs, retiredDate: { type: 'date' },
        prospect: { type: 'combo', options: YES_NO }, sampleReceiptNotification: { type: 'combo', options: YES_NO },
        reportDelivery: { type: 'combo', options: YES_NO }, invoiceDelivery: { type: 'combo', options: YES_NO },
      };
    }
    return {
      client: text, address1: text, address2: text, zip: text, phone: text, fax: text, webSite: text, mobilePhone: text, homePhone: text,
      ...geoSpecs,
      account: { type: 'combo', options: (r) => (accountsByClient[r.oid] ?? []).map((a) => a.name) },
      owner: { type: 'combo', options: owners.map((o) => o.name) },
      preferredContactMethod: { type: 'combo', options: PREFERRED_METHODS },
      contractDate: { type: 'date' },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, isProjects, isContacts, geoSpecs, projCats, clientNames, owners, accountsByClient]);
  const columns = useMemo(() => {
    const base = (isContacts ? CONTACT_COLUMNS : isProjects ? PROJECT_COLUMNS : CLIENT_COLUMNS) as AppColumn<Row>[];
    return base.map((c) => (editSpecs[c.field] ? { ...c, edit: editSpecs[c.field] } : c));
  }, [isContacts, isProjects, editSpecs]);
  const hasRowEdit = Object.keys(editSpecs).length > 0;
  /** Reads the choices that belong to one row (a client's account numbers) when that row opens for editing. */
  const onBeginRowEdit = (row: Row) => {
    if (isContacts || isProjects || accountsByClient[row.oid]) return;
    void api.get(`/api/v1/clients/${encodeURIComponent(row.oid)}/account-numbers`)
      .then((r) => {
        const list = ((r.data as { data?: { oid: string; accountNumber: string; suspend?: boolean }[] }).data ?? [])
          .filter((a) => !a.suspend).map((a) => ({ id: a.oid, name: a.accountNumber }));
        setAccountsByClient((m) => ({ ...m, [row.oid]: list }));
      }).catch(() => { /* the Account dropdown simply stays empty */ });
  };
  const textOf = (v: unknown) => (v == null ? '' : String(v));
  /** Inline row save — the PUTs are full replaces, so the stored record is read first and the edited cells are merged over it. */
  const saveInline = async (row: Row, values: Record<string, string>) => {
    try {
      if (isContacts) {
        const c = row as MaintContactRow;
        // A contact with no client (a Prospect contact) is edited through the same routes; the API ignores the client segment.
        const routeClient = c.clientOid ?? '00000000-0000-0000-0000-000000000000';
        const res = await api.get(`/api/v1/clients/${encodeURIComponent(routeClient)}/contacts/${c.oid}`);
        const full = (res.data as { data?: Record<string, unknown> }).data ?? {};
        const orig = c as unknown as Record<string, unknown>;
        const changed = (k: string) => k in values && values[k] !== textOf(k === 'retiredDate' ? String(orig[k] ?? '').slice(0, 10) : orig[k]);
        const body = { ...full, type: c.typeCode } as Record<string, unknown>;
        // Text cells go straight in (JobTitle is its own column, not Position).
        for (const k of ['email', 'officePhone', 'mobilePhone', 'otherPhone', 'zip', 'jobTitle']) if (k in values) body[k] = values[k];
        // Lookups: a changed name is mapped back to its id, an untouched one keeps the stored id.
        const countryId = changed('country') ? idOf(countries, values.country) : (full.countryId ?? null);
        body.countryId = countryId;
        body.stateId = changed('state') || changed('country')
          ? (states.find((s) => s.name === values.state && (!countryId || s.parentId === countryId))?.id ?? null) : (full.stateId ?? null);
        body.cityId = changed('city') || changed('state') || changed('country')
          ? (cities.find((x) => x.name === values.city && x.parentId === body.stateId)?.id ?? null) : (full.cityId ?? null);
        if ('retiredDate' in values) body.retiredDate = values.retiredDate ? values.retiredDate.slice(0, 10) : null;
        for (const k of ['prospect', 'sampleReceiptNotification', 'reportDelivery', 'invoiceDelivery']) if (k in values) body[k] = values[k] === 'Yes';
        await api.put(`/api/v1/clients/${encodeURIComponent(routeClient)}/contacts/${c.oid}`, body);
      } else if (isProjects) {
        const p = row as MaintProjectRow;
        if (!p.clientOid) throw new Error('no client');
        const res = await api.get(`/api/v1/clients/${encodeURIComponent(p.clientOid)}/projects/${p.oid}`);
        const full = (res.data as { data?: Record<string, unknown> }).data ?? {};
        const body: Record<string, unknown> = { ...full };
        for (const k of ['projectId', 'projectName', 'projectLocation', 'projectCode']) if (k in values) body[k] = values[k];
        if (!String(body.projectId ?? '').trim()) { notify.warning("'Project ID' must not be empty."); throw new Error('required'); }
        if ('projectCategory' in values && values.projectCategory !== textOf(p.projectCategory)) body.projectCategoryId = idOf(projCats, values.projectCategory);
        // The URL carries the client: changing the Client cell moves the project to that client.
        let owner = p.clientOid;
        if ('client' in values && values.client !== textOf(p.client)) {
          const nid = idOf(clientNames, values.client);
          if (!nid) { notify.warning("'Client' must not be empty."); throw new Error('required'); }
          owner = nid;
        }
        await api.put(`/api/v1/clients/${encodeURIComponent(owner)}/projects/${p.oid}`, body);
      } else {
        const res = await api.get(`/api/v1/clients/${encodeURIComponent(row.oid)}`);
        const f = (res.data as { data?: Record<string, unknown> }).data ?? {};
        const v = (k: string) => (k in values ? values[k] : undefined);
        const name = (v('client') ?? String(f.customerName ?? '')).trim();
        if (!name) { notify.warning("'Client' must not be empty."); throw new Error('required'); }
        const orig = row as unknown as Record<string, unknown>;
        const changed = (k: string) => k in values && values[k] !== textOf(k === 'contractDate' ? String(orig[k] ?? '').slice(0, 10) : orig[k]);
        // Lookups: a changed name is mapped back to its id, an untouched one keeps the stored id.
        const countryId = changed('country') ? idOf(countries, values.country) : (f.countryId ?? null);
        const stateId = changed('state') || changed('country')
          ? (states.find((s) => s.name === values.state && (!countryId || s.parentId === countryId))?.id ?? null) : (f.stateId ?? null);
        const cityId = changed('city') || changed('state') || changed('country')
          ? (cities.find((x) => x.name === values.city && x.parentId === stateId)?.id ?? null) : (f.cityId ?? null);
        const method = changed('preferredContactMethod') ? Math.max(0, PREFERRED_METHODS.indexOf(values.preferredContactMethod ?? '')) : f.preferredContactMethod;
        await api.put(`/api/v1/clients/${encodeURIComponent(row.oid)}`, {
          customerName: name, clientNumber: f.clientNumber, clientCode: f.clientCode,
          officePhone: v('phone') ?? f.officePhone, webSite: v('webSite') ?? f.webSite, fax: v('fax') ?? f.fax,
          mobilePhone: v('mobilePhone'), homePhone: v('homePhone'),
          accountId: changed('account') ? idOf(accountsByClient[row.oid] ?? [], values.account) : f.accountId,
          categoryId: f.categoryId, industryId: f.industryId, primaryContactId: f.primaryContactId,
          email: f.email, licenseNumber: f.licenseNumber, customerId: f.customerId, retiredDate: f.retiredDate,
          address: v('address1') ?? f.address, address1: v('address2') ?? f.address1, postCode: f.postCode,
          countryId, stateId, cityId, zip: v('zip') ?? f.zip, sicCode: f.sicCode,
          ownerId: changed('owner') ? idOf(owners, values.owner) : f.ownerId, sourceLeadId: f.sourceLeadId, creditLimit: f.creditLimit,
          preferredContactMethod: method,
          contractDate: 'contractDate' in values ? (changed('contractDate') ? (values.contractDate || null) : f.contractDate) : f.contractDate,
          startDate: f.startDate, manintenanceStartDate: f.manintenanceStartDate,
          manintenanceEndDate: f.manintenanceEndDate, acceptanceDate: f.acceptanceDate, reminderDate: f.reminderDate,
          mail: f.mail, sendEmail: f.sendEmail,
        });
      }
      notify.success('Saved successfully.');
      void load();
    } catch (e) {
      if ((e as Error).message !== 'required') {
        const serverMsg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        notify.error(serverMsg ? `Failed to save the ${t.noun}. ${serverMsg}` : `Failed to save the ${t.noun}.`);
      }
      throw e;   // keeps the row in edit mode
    }
  };
  const segment = isContacts ? 'contacts' : isProjects ? 'projects' : 'clients';
  const url = isContacts ? `${MAINT_BASE}/contacts`
    : isProjects ? `${MAINT_BASE}/projects?scope=${projectRetired ? 'retired' : 'active'}`
    : `${MAINT_BASE}/clients?scope=${readOnly ? 'retired' : 'active'}`;

  /** keepSelection: the Refresh button reloads the grid but leaves ticked rows ticked (those still in the list); every other
   *  reload (after a save / delete / reactivate) clears the ticks, as G5 does once an action is submitted. */
  const load = useCallback(async (keepSelection = false) => {
    const mine = ++seq.current;
    const t0 = performance.now();
    setLoading(true);
    try {
      const res = await api.get<{ data?: Row[] }>(url);
      if (mine !== seq.current) return;
      const fresh = res.data?.data ?? [];
      setRows(fresh);
      setSelected((prev) => (keepSelection ? new Set([...prev].filter((id) => fresh.some((r) => r.oid === id))) : new Set()));
    } catch {
      if (mine === seq.current) notify.error(`Could not load the ${t.nouns}.`);
    } finally {
      if (mine === seq.current) { setLoading(false); setLoadMs(performance.now() - t0); }
    }
  }, [url, notify, t.nouns]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => rows.filter((r) => matchesSearch(r, columns, search)), [rows, columns, search]);
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.oid)), [rows, selected]);

  // ── Edit ──────────────────────────────────────────────────────────────────────
  const edit = (row?: Row) => {
    const r = row ?? (selectedRows.length === 1 ? selectedRows[0] : undefined);
    if (!r) { notify.warning(`Select one ${t.noun} to edit.`); return; }
    if (isProjects) {
      const pr = r as MaintProjectRow;
      // A project or contact with no client opens too (G5 does) — the Client lookup on the form can file it.
      setProjectDlg({ clientId: pr.clientOid ?? '', clientName: pr.client ?? '', projectOid: pr.oid });
    } else if (isContacts) {
      const c = r as MaintContactRow;
      setContactDlg({ clientId: c.clientOid ?? '', clientName: c.client ?? '', type: c.typeCode, contactOid: c.oid });
    } else {
      setEditClient(r.oid);
    }
  };

  // ── New ───────────────────────────────────────────────────────────────────────
  const startNew = async () => {
    if (!isContacts && !isProjects) { setEditClient(null); setNewClient(true); setNewSeq((n) => n + 1); return; }
    // A project opens straight to the form — its own Client picker (required) chooses the client, so no popup first.
    if (isProjects) { setProjectDlg({ clientId: '', clientName: '', projectOid: null }); setNewSeq((n) => n + 1); return; }
    // A contact opens straight to the form too — its own Client / Prospect lookup files it, as G5's detail does.
    setContactDlg({ clientId: '', clientName: '', type: 0, contactOid: null }); setNewSeq((n) => n + 1);
  };

  // ── Delete (G5 guards run on the server; the first refusal stops the batch, as G5 does) ──
  const remove = () => {
    if (selectedRows.length === 0) { notify.warning(`Select at least one ${t.noun} to delete.`); return; }
    const n = selectedRows.length;
    void runDelete({
      formName: MAINT_FORM_NAME,
      message: n === 1 ? `Delete this ${t.noun}?` : `Delete ${n} ${t.nouns}?`,
      delete: async () => {
        try {
          for (const r of selectedRows) await api.delete(`${MAINT_BASE}/${segment}/${r.oid}`);
        } finally {
          await load();   // rows deleted before a refusal are gone — show the truth either way
        }
      },
      onDeleted: () => setSelected(new Set()),
    });
  };

  // ── Reactivate (Retired Clients only — G5 ClientRetireViewController "Reactivate") ──
  const reactivate = async () => {
    if (selectedRows.length === 0) { notify.warning('Please select the Customer.'); return; }
    const n = selectedRows.length;
    if (!(await confirm({ title: 'Reactivate', message: n === 1 ? 'Reactivate this client?' : `Reactivate ${n} clients?` }))) return;
    try {
      await api.post(`${MAINT_BASE}/clients/reactivate`, { oids: selectedRows.map((r) => r.oid) });
      notify.success('Reactivated successfully.');
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Could not reactivate.');
    }
    await load();
  };

  const exportData = useMemo(
    () => ({ filename: `Maintenance - ${t.crumb}`, ...gridExportMatrix(shown, columns) }),
    [shown, columns, t.crumb],
  );

  // ── Full-page detail (rule 12): the list is replaced while a record is open ─────
  const closeDetail = () => { clearMaintTabMemory(); setEditClient(null); setNewClient(false); setContactDlg(null); setProjectDlg(null); };
  const ids = shown.map((r) => r.oid);
  const neighbours = (current: string | null) => {
    const i = current ? ids.indexOf(current) : -1;
    return { prevId: i > 0 ? (ids[i - 1] ?? null) : null, nextId: i >= 0 && i < ids.length - 1 ? (ids[i + 1] ?? null) : null };
  };
  const openById = (id: string) => { const r = rows.find((x) => x.oid === id); if (r) edit(r); };
  const framePage = (current: string | null, onNew: () => void) => ({
    listTitle: t.crumb, onHome, onNew, onNavigate: openById, ...neighbours(current),
    // G5's record actions across the top of the page: New · Delete · Refresh · Audit Trail.
    onRefresh: () => setRefreshSeq((n) => n + 1),
    recordId: current,
    deleteUrl: current ? `${MAINT_BASE}/${segment}/${current}` : null,
    deleteNoun: t.noun,
    onDeleted: () => { closeDetail(); void load(); },
  });

  if (editClient !== null || newClient) {
    return (
      <ClientDetailModal
        key={`${editClient ?? `new-${newSeq}`}-${refreshSeq}`}
        clientId={editClient}
        notify={notify}
        page={framePage(editClient, () => { setEditClient(null); setNewClient(true); setNewSeq((n) => n + 1); })}
        onClose={() => { closeDetail(); void load(); }}
        onSaved={(id) => { void load(); if (id && !editClient) { setNewClient(false); setEditClient(id); } }}
      />
    );
  }
  if (projectDlg) {
    const d = projectDlg;
    return (
      <ProjectDetailModal
        key={`${d.projectOid ?? `new-${newSeq}`}-${refreshSeq}`}
        clientId={d.clientId} clientName={d.clientName} projectOid={d.projectOid}
        notify={notify}
        page={framePage(d.projectOid, () => { setProjectDlg({ clientId: '', clientName: '', projectOid: null }); setNewSeq((n) => n + 1); })}
        onClose={() => { closeDetail(); void load(); }}
        onSaved={(id, owner) => { void load(); if (id && !d.projectOid) setProjectDlg({ ...d, clientId: owner || d.clientId, projectOid: id }); }}
      />
    );
  }
  if (contactDlg) {
    const d = contactDlg;
    return (
      <ContactDetailModal
        key={`${d.contactOid ?? `new-${newSeq}`}-${refreshSeq}`}
        clientId={d.clientId} clientName={d.clientName} type={d.type} contactOid={d.contactOid}
        showClientOnNew
        notify={notify}
        page={framePage(d.contactOid, () => { setContactDlg({ clientId: '', clientName: '', type: 0, contactOid: null }); setNewSeq((n) => n + 1); })}
        onClose={() => { closeDetail(); void load(); }}
        onSaved={(id) => { void load(); if (id && !d.contactOid) setContactDlg({ ...d, contactOid: id }); }}
      />
    );
  }

  return (
    <div className="bl-page mlv-host maint-page">
      {/* The same top section as every Utility list (ModuleListView): breadcrumb, then the List View / Detail View switch, then the
          button row (with the load-time chip at its end), then the search row with the Export / Maximize icons at its right end. */}
      <div className="bl-top">
        <div className="dp-breadcrumb" style={{ paddingTop: 12 }}>
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Maintenance</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{t.crumb}</span>
        </div>

        {/* Detail View opens the ticked record, or the first row (not on the read-only Retired Clients list). */}
        {!readOnly && (
          <div style={{ padding: '2px 0 0' }}>
            <ViewToggle
              view="list"
              detailDisabled={shown.length === 0}
              onChange={(v) => {
                if (v !== 'detail') return;
                const r = selectedRows.length === 1 ? selectedRows[0] : shown[0];
                if (r) edit(r);
              }}
            />
          </div>
        )}

        {/* The title lives in the top bar; this row only publishes the load-time chip (and Audit) to the toolbar, as on Utility. */}
        <div className="dp-page-hdr mlv-hdr--empty">
          <div className="dp-title-row">
            <FormTitle pageId={pageId} loadMs={loadMs} loading={loading} auditFormName={MAINT_FORM_NAME} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div className="mlv-toolbar-row">
          <AppToolbar
            perm={perm}
            crudFirst
            left={readOnly ? (
              <button className="ca-btn" onClick={() => void reactivate()} disabled={loading || selectedRows.length === 0}
                title="Bring the ticked clients back to Clients">
                <span className="tb-i">↩</span> Reactivate
              </button>
            ) : undefined}
            beforeExport={isProjects ? (
              <button className="ca-btn" onClick={() => setProjectRetired((v) => !v)} disabled={loading}
                title={projectRetired ? 'Back to the open projects' : 'Show the retired projects'}>
                <span className="tb-i">{projectRetired ? '↩' : '🗄'}</span> {projectRetired ? 'Active' : 'Retired'}
              </button>
            ) : undefined}
            onNew={readOnly ? undefined : () => void startNew()}
            newDisabled={loading}
            onEdit={readOnly ? undefined : () => edit()}
            editDisabled={loading || selectedRows.length !== 1}
            onDelete={readOnly ? undefined : remove}
            deleteDisabled={loading || selectedRows.length === 0}
            onRefresh={() => void load(true)}
            refreshing={loading}
            exportData={exportData}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input
              type="text" placeholder="Search…" aria-label="Search"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" {...exportData} />
            <button
              className={`dv-gridact${fullGrid ? ' dv-on' : ''}`}
              onClick={() => setFullGrid((v) => !v)}
              title={fullGrid ? 'Restore' : 'Maximize'}
            >
              <i className={`ti ${fullGrid ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} />
            </button>
          </div>
        </div>

        <AppDataGrid<Row>
          rows={shown}
          columns={columns}
          getRowId={(r) => r.oid}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowDoubleClick={readOnly ? undefined : (r) => edit(r)}
          rowEdit={hasRowEdit ? { onSave: saveInline, onBegin: onBeginRowEdit } : undefined}
          onRowOpen={readOnly ? undefined : (r) => edit(r)}
          rowEditIcon="ti-edit"
          defaultPageSize={20}
          exportFilename={`Maintenance - ${t.crumb}`}
          emptyText={t.empty}
          initialSort={isProjects ? { key: 'projectId', dir: 'desc' } : undefined}
          hideHeaderTools
          maximized={fullGrid}
          onMaximizeChange={setFullGrid}
        />
      </div>

    </div>
  );
}
